import * as React from 'react';
import io from 'socket.io-client';
import { selectAuthUser } from '../slices/auth-slice';
import { useSelector } from 'react-redux';

const WebSocketContext = React.createContext<any>(null);
WebSocketContext.displayName = 'WebSocketContext';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

/*
 * Dodaje informacije o backendu event podacima.
 *
 * Primjer:
 *
 * PET -> { ...ticket, backendName: 'PET' }
 * S1  -> { ...ticket, backendName: 'S1' }
 *
 * Socket.IO može poslati i:
 *
 * ['event-name', payload]
 */
function addBackendToEventData(
  data: any,
  backendName: string
): any {
  /*
   * Socket.IO može poslati:
   *
   * ['event-name', payload]
   */
  if (
    Array.isArray(data) &&
    typeof data[0] === 'string'
  ) {
    return [
      data[0],
      addBackendToEventData(
        data[1],
        backendName
      ),
    ];
  }

  /*
   * Ako je payload lista.
   */
  if (Array.isArray(data)) {
    return data.map((item) =>
      addBackendToEventData(
        item,
        backendName
      )
    );
  }

  /*
   * Ako je konkretan objekat/event.
   */
  if (
    data &&
    typeof data === 'object'
  ) {
    return {
      ...data,
      backendName,
    };
  }

  return data;
}

export function WebSocketProvider({
  children,
}: any) {
  const [ws, setWs] =
    React.useState<any>(null);

  const user = useSelector(selectAuthUser);

  /*
   * Koristimo token korisnika kao dependency
   * kako bi se socketi ponovo napravili nakon login-a.
   */
  const authToken = user?.token;

  React.useEffect(() => {
    const backendTokens =
      getBackendAuthTokens();

    /*
     * Ako nema niti jednog uspješno
     * autentifikovanog backenda,
     * nema socket konekcija.
     */
    if (!backendTokens.length) {
      setWs(null);
      return;
    }

    /*
     * Svi listeneri koje stranice registruju
     * preko:
     *
     * ws.on(...)
     * ws.off(...)
     */
    const listeners: Record<
      string,
      Set<(...args: any[]) => void>
    > = {};

    /*
     * Svi socketi prema backendima.
     */
    const sockets: {
      backend: BackendAuthToken;
      socket: any;
    }[] = [];

    /*
     * Agregirani WebSocket objekat.
     *
     * Stranice i dalje koriste:
     *
     * ws.on(...)
     * ws.off(...)
     * ws.emit(...)
     *
     * a ovdje se to povezuje sa više
     * backend socket konekcija.
     */
    const aggregatedWs = {
      /*
       * Registracija listenera.
       */
      on: (
        eventName: string,
        callback: (...args: any[]) => void
      ) => {
        if (!listeners[eventName]) {
          listeners[eventName] =
            new Set();
        }

        listeners[eventName].add(
          callback
        );
      },

      /*
       * Uklanjanje listenera.
       */
      off: (
        eventName: string,
        callback?: (...args: any[]) => void
      ) => {
        if (!listeners[eventName]) {
          return;
        }

        if (callback) {
          listeners[eventName].delete(callback);
        } else {
          listeners[eventName].clear();
        }
      },

      /*
       * Emit:
       *
       * 1. risk.operator.reconfig
       *    šaljemo prema SVIM backendima.
       *
       * 2. lokalni listeneri se uvijek
       *    izvršavaju kao i ranije.
       */
      emit: (
        eventName: string,
        ...args: any[]
      ) => {
        /*
         * Filteri Risk stranice moraju biti
         * poslani na svaki backend.
         */
        if (
          eventName ===
          'risk.operator.reconfig'
        ) {
          sockets.forEach(
            ({
              backend,
              socket,
            }) => {

              socket.emit(
                eventName,
                args[0]
              );
            }
          );
        }

        /*
         * Pozovi lokalne listenere.
         */
        listeners[eventName]?.forEach(
          (callback) => {
            callback(...args);
          }
        );
      },

      /*
       * Disconnect svih backend socketova.
       */
      disconnect: () => {
        sockets.forEach(
          ({ socket }) => {
            socket.disconnect();
          }
        );
      },
    };

    /*
     * ============================================================
     * KREIRAJ SOCKET ZA SVAKI AUTENTIFIKOVANI BACKEND
     * ============================================================
     */
    backendTokens.forEach(
      (backend) => {
        const socketUrl =
          backend.url.replace(/\/$/, '');


        const socket = io(
          socketUrl,
          {
            path: '/socket.io',

            auth: {
              token: backend.token,
              appName: 'admin',
            },
          }
        );

        sockets.push({
          backend,
          socket,
        });

        /*
         * ========================================================
         * CONNECT
         * ========================================================
         */
        socket.on(
          'connect',
          () => {

          }
        );

        /*
         * ========================================================
         * RECONNECT
         * ========================================================
         *
         * RiskVerificationTable sluša:
         *
         * ws.on('reconnect', ...)
         *
         * Kada se jedan backend reconnectuje,
         * obavijesti agregirani ws.
         */
        socket.on(
          'reconnect',
          () => {
            aggregatedWs.emit(
              'reconnect',
              backend.name
            );
          }
        );

        /*
         * ========================================================
         * DISCONNECT
         * ========================================================
         */
        socket.on(
          'disconnect',
          (reason: any) => {

          }
        );

        /*
         * ========================================================
         * CONNECTION ERROR
         * ========================================================
         */
        socket.on(
          'connect_error',
          (error: any) => {
            console.error(
              `[WS] ${backend.name}: connection error`,
              error
            );
          }
        );

        /*
         * ========================================================
         * EVENTS - LIVE
         * ========================================================
         */
        socket.on(
          'events.live.update',
          (data: any) => {
            aggregatedWs.emit(
              'events.live.update',
              addBackendToEventData(
                data,
                backend.name
              )
            );
          }
        );

        /*
         * ========================================================
         * EVENTS - NO LIVE
         * ========================================================
         */
        socket.on(
          'events.nolive.update',
          (data: any) => {
            aggregatedWs.emit(
              'events.nolive.update',
              addBackendToEventData(
                data,
                backend.name
              )
            );
          }
        );

        /*
         * ========================================================
         * RISK VERIFICATION
         * ========================================================
         *
         * OVO JE BITNO ZA RISK.
         *
         * Backend može poslati:
         *
         * risk.verification
         *
         * Mi dodajemo backendName i prosljeđujemo
         * event RiskVerificationTable komponenti.
         */
        socket.on(
          'risk.verification',
          (data: any) => {


            aggregatedWs.emit(
              'risk.verification',
              addBackendToEventData(
                data,
                backend.name
              )
            );
          }
        );

        /*
         * ========================================================
         * APP RELOAD
         * ========================================================
         */
        socket.on(
          'app-reload-admin',
          () => {

            window.location.reload();
          }
        );
      }
    );

    /*
     * Stranice dobijaju jedan ws objekat,
     * iako iza njega postoji više socket
     * konekcija.
     */
    setWs(aggregatedWs);

    /*
     * ============================================================
     * CLEANUP
     * ============================================================
     */
    return () => {
      sockets.forEach(
        ({
          backend,
          socket,
        }) => {

          socket.disconnect();
        }
      );

      setWs(null);
    };
  }, [authToken]);

  return (
    <WebSocketContext.Provider
      value={ws}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context =
    React.useContext(
      WebSocketContext
    );

  if (!context) {
    throw new Error(
      'useWebSocket must be used inside a <WebSocketProvider /> component'
    );
  }

  return context;
}